import { query } from "./dom.js";

/**
 * Corporate logo carousel.
 *
 * The viewport is natively scrollable, so the visitor can drag it left/right
 * with a trackpad, finger, or (via the mousedown/mousemove drag handler
 * below) a plain mouse — the browser has no native gesture for that last one.
 * When idle (not touching / hovering the carousel) we advance it each frame
 * to auto-scroll.
 *
 * The track holds two identical sets of logos so the loop can be infinite in
 * BOTH directions while only ever showing the 10 real logos, without us
 * having to intercept every possible scroll source individually (wheel,
 * touch, and our own mouse-drag handler all move `scrollLeft` in ways we
 * can't uniformly hook beforehand). Instead we park the starting position at
 * the seam between the two sets — visually identical to position 0, since
 * the sets are pixel-for-pixel copies — so there's a full set's width of
 * native scroll room on EITHER side before a gesture could ever reach the
 * track's true (dead-end) start or end. A single `scroll` listener then
 * recentres back to the seam once a gesture strays too close to either real
 * edge; because the sets are identical, that jump is invisible.
 *
 * Notes for mobile (iOS in particular):
 *  - We keep our own float `position` and WRITE scrollLeft from it during
 *    auto-scroll, adjusting it by the recentring listener rather than ever
 *    reading scrollLeft back mid-animation — iOS rounds scrollLeft to an
 *    integer, and reading it back would round away our sub-pixel-per-frame
 *    step and stall the animation.
 *  - Pausing is driven by a "last interaction" timestamp, not a sticky
 *    boolean. iOS can fire `mouseenter` on a tap without ever firing
 *    `mouseleave`, so a boolean flag would get stuck; the timestamp always
 *    lapses and auto-scroll resumes on its own once the finger is idle.
 */

const SECONDS_PER_SET = 57;   // time to traverse one logo set (matches old CSS)
const IDLE_RESUME_MS = 1200;  // resume this long after the last interaction

export function initCorporateCarousel() {
  const carousel = query('[data-action="corporate-carousel"]');
  if (!carousel) return;

  const viewport = query(".corporate__carousel-viewport", carousel);
  const track = query(".corporate__carousel-track", carousel);
  if (!viewport || !track) return;

  // Honour reduced-motion: no auto-scroll, but manual scrolling still works.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Only hover-capable pointers (mouse) pause on hover. On touch devices the
  // synthesized hover events are unreliable, so we lean on interaction alone.
  const canHover = window.matchMedia("(hover: hover)").matches;

  const setWidth = () => track.scrollWidth / 2;   // two identical sets

  // Start at the seam so there's a full set's width of native scroll room
  // behind us as well as ahead — see file header.
  viewport.scrollLeft = setWidth();

  let position = viewport.scrollLeft;          // our authoritative float offset
  let hovering = false;
  let lastInteraction = Number.NEGATIVE_INFINITY; // start scrolling immediately
  let lastFrame = null;

  const mark = () => { lastInteraction = performance.now(); };

  // Direct input signals — NOT the `scroll` event, which our own writes fire.
  viewport.addEventListener("wheel", mark, { passive: true });
  viewport.addEventListener("touchstart", mark, { passive: true });
  viewport.addEventListener("touchmove", mark, { passive: true });
  viewport.addEventListener("pointerdown", mark);
  viewport.addEventListener("mousedown", mark);

  // Trackpad swipes and touch drags scroll the viewport natively, but a
  // plain mouse drag does not — the browser only scrolls a container from
  // actual wheel/touch gestures, and our scrollbar is hidden. Without this,
  // mouse users have no way to move the carousel by hand at all.
  viewport.addEventListener("mousedown", (e) => {
    e.preventDefault();   // stop native image-drag ghosting and text selection

    const dragStartX = e.clientX;
    const dragStartScroll = viewport.scrollLeft;

    function onMove(e) {
      mark();
      const w = setWidth();
      const target = dragStartScroll - (e.clientX - dragStartX);
      // The `scroll` recentring listener below is async (the browser doesn't
      // fire `scroll` synchronously from a `scrollLeft` write), so a very
      // fast drag can outrun it before the next event has a chance to run.
      // For the one input source we fully control, wrap synchronously too.
      // Always into [0, w) — NOT [w, 2w) — because the true scrollable max
      // is 2w minus the viewport's own width, so the top of a full second
      // "w" would sometimes exceed it and get silently clamped, sticking the
      // drag dead at the last real logo instead of wrapping.
      viewport.scrollLeft = w > 0 ? ((target % w) + w) % w : target;
    }

    function onUp() {
      mark();
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  });

  if (canHover) {
    viewport.addEventListener("mouseenter", () => { hovering = true; });
    viewport.addEventListener("mouseleave", () => { hovering = false; });
  }

  // Recentre on ANY scroll — wheel, touch, mouse drag, or our own auto-scroll
  // writes — once it strays close to a real edge of the (double-length)
  // track, jumping by exactly one set's width so the visitor never actually
  // reaches a dead end. A generous buffer (capped, but scaling down for
  // narrow tracks) means one fast flick can't outrun the correction.
  viewport.addEventListener("scroll", () => {
    const w = setWidth();
    if (w <= 0) return;

    const max = track.scrollWidth - viewport.clientWidth; // true scrollable ceiling
    const buffer = Math.min(300, w * 0.3);

    if (viewport.scrollLeft < buffer) {
      viewport.scrollLeft += w;
      position += w;
    } else if (viewport.scrollLeft > max - buffer) {
      viewport.scrollLeft -= w;
      position -= w;
    }
  }, { passive: true });

  function tick(now) {
    if (lastFrame === null) lastFrame = now;
    const dt = now - lastFrame;
    lastFrame = now;

    const idle = !hovering && now - lastInteraction > IDLE_RESUME_MS;

    if (idle) {
      position += (setWidth() / SECONDS_PER_SET) * (dt / 1000);
      viewport.scrollLeft = position;
    } else {
      // The visitor is in control — follow the real position so auto-scroll
      // resumes seamlessly from wherever they left it.
      position = viewport.scrollLeft;
    }

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}
